import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { UserAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

const getDateFromField = (dateField) => {
  if (!dateField) return null;
  
  // If it's a Firestore Timestamp
  if (dateField.toDate) {
    return dateField.toDate();
  }
  
  // If it's a string, try to parse it
  if (typeof dateField === 'string') {
    return new Date(dateField);
  }
  
  // If it's already a Date object
  if (dateField instanceof Date) {
    return dateField;
  }
  
  return null;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState({
    payables: 0,
    receivables: 0,
    deliveries: 0,
    inbox: 0,
    total: 0
  });
  
  const { user } = UserAuth();

  useEffect(() => {
    // Don't fetch notifications if user is not authenticated
    if (!user) {
      setNotifications({
        payables: 0,
        receivables: 0,
        deliveries: 0,
        inbox: 0,
        total: 0
      });
      return;
    }
    
    const fetchNotifications = async () => {
      try {
        const today = new Date();
        let payablesCount = 0;
        let receivablesCount = 0;
        let deliveriesCount = 0;
        let inboxCount = 0;

        // Fetch payables due
        const payablesSnap = await getDocs(query(
          collection(db, 'payables'),
          where('status', '==', 'Open')
        ));
        payablesSnap.forEach(doc => {
          const data = doc.data();
          const dueDate = getDateFromField(data.dueDate);
          if (dueDate && dueDate <= today) {
            payablesCount++;
          }
        });

        // Fetch receivables due
        const receivablesSnap = await getDocs(query(
          collection(db, 'receivable'),
          where('status', '==', 'Open')
        ));
        receivablesSnap.forEach(doc => {
          const data = doc.data();
          const dueDate = getDateFromField(data.dueDate);
          if (dueDate && dueDate <= today) {
            receivablesCount++;
          }
        });

        // Fetch deliveries due
        const deliveriesSnap = await getDocs(query(
          collection(db, 'po'),
          where('status', '==', 'Pending')
        ));
        deliveriesSnap.forEach(doc => {
          const poData = doc.data();
          const poNumber = poData.poNumber || '';
          
          if (poNumber.length >= 6) {
            const dateStr = poNumber.substring(0, 6);
            const month = parseInt(dateStr.substring(0, 2)) - 1;
            const day = parseInt(dateStr.substring(2, 4));
            const year = 2000 + parseInt(dateStr.substring(4, 6));
            
            const poDate = new Date(year, month, day);
            const dueDate = new Date(poDate);
            dueDate.setDate(dueDate.getDate() + 30);
            
            if (dueDate <= today) {
              deliveriesCount++;
            }
          }
        });

        // Fetch unread inbox messages (count docs where read !== true, including missing field)
        const inboxSnap = await getDocs(collection(db, 'inbox'));
        inboxSnap.forEach(doc => {
          const data = doc.data();
          if (data.read !== true) inboxCount++;
        });

        setNotifications({
          payables: payablesCount,
          receivables: receivablesCount,
          deliveries: deliveriesCount,
          inbox: inboxCount,
          total: payablesCount + receivablesCount + deliveriesCount + inboxCount
        });

      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 300000);

    // Real-time updates for inbox unread count
    const unsubscribeInbox = onSnapshot(collection(db, 'inbox'), (snapshot) => {
      let inboxCount = 0;
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.read !== true) inboxCount++;
      });
      setNotifications(prev => ({
        ...prev,
        inbox: inboxCount,
        total: (prev.payables || 0) + (prev.receivables || 0) + (prev.deliveries || 0) + inboxCount
      }));
    });

    return () => {
      clearInterval(interval);
      unsubscribeInbox();
    };
  }, [user]); // Add user as dependency

  return (
    <NotificationContext.Provider value={notifications}>
      {children}
    </NotificationContext.Provider>
  );
}; 